import { useCallback } from 'react';

import type { TrackingProperties } from '@change-corgi/tracking-properties';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import type { FormValues } from '#src/app/pages/petitionDetails/shared/types';
import { useQueryParamValue } from '#src/app/shared/hooks/location';
import { useSessionAsync } from '#src/app/shared/hooks/session';
import { isLoaded } from '#src/app/shared/utils/async';

export type SignatureIntentTrackingResult = Readonly<{
	trackSignIntent: (formData: FormValues) => void;
	trackSignIntentValid: (formData: FormValues) => void;
}>;

type SessionAsync = ReturnType<typeof useSessionAsync>;

function getSessionGeoPayload(session: SessionAsync) {
	if (!isLoaded(session)) {
		return { ipCountryCode: undefined, geoipData: undefined };
	}
	const { ipCountry, geoipData } = session.value;
	return {
		ipCountryCode: ipCountry?.countryCode ?? undefined,
		geoipData,
	};
}

export function useSignatureIntentTrackingData(): (formData: FormValues) => TrackingProperties {
	const {
		data: { petition },
	} = usePetitionDetailsPageData();
	const session = useSessionAsync();
	const recruiterUuid = useQueryParamValue('recruited_by_uuid');

	return useCallback(
		(formData: FormValues): TrackingProperties => {
			const { ipCountryCode, geoipData } = getSessionGeoPayload(session);

			return {
				user_country_code: formData.countryCode,
				petition_id: petition.id,
				public: formData.public,
				share_info_with_organization: formData.shareInfoWithOrganization,
				reasons_for_signing_completed: !!formData.reasonForSigning,
				ip_country_code: ipCountryCode,
				ip_city: geoipData?.city ?? undefined,
				ip_postal_code: geoipData?.postalCode ?? undefined,
				ip_region: geoipData?.region ?? undefined,
				recruited_by_uuid: recruiterUuid,
				context: 'petitions_show',
				phone_number_filled_out: !!formData.phoneNumber,
				address_filled_out: !!formData.address,
			};
		},
		[petition, recruiterUuid, session],
	);
}
